/* eslint-disable react/jsx-no-bind */
import { useIntl } from 'react-intl';
import { Info } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Label, Checkbox } from '@hqo/ui-atoms';
import { Controller, Control, FieldErrors, UseFormSetValue, useWatch } from 'react-hook-form';
import { addMonths, differenceInCalendarMonths, isBefore } from 'date-fns';
import { CreateVisitFormV2Values, RepeatType } from '../../../types/form-data.types';
import { DatePickerField } from 'components/v2/date-picker-button';
import { useRepeatSection } from '../hooks/use-repeat-section.hook';
import { Tip } from 'components/shared/tip';
import { useCallback } from 'react';
import { useValidationMessage } from '../../../hooks/use-validation-message.hook';

interface RepeatSectionProps {
  control: Control<CreateVisitFormV2Values>;
  errors: FieldErrors<CreateVisitFormV2Values>;
  setValue: UseFormSetValue<CreateVisitFormV2Values>;
  visitDate: Date | null;
  /** Building-level cap (maxFutureInvite) — when set, tightens the 6-month default. */
  maxFutureInviteDate?: Date;
  triggerValidation?: () => Promise<boolean | void>;
}

export const RepeatSection = ({
  control,
  errors,
  setValue,
  visitDate,
  maxFutureInviteDate,
  triggerValidation: _triggerValidation,
}: RepeatSectionProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const formatValidationMessage = useValidationMessage();
  const repeatType = useWatch({ control, name: 'repeatType' }) as RepeatType;
  const recurrenceDays = useWatch({ control, name: 'recurrenceDays' });
  const setRepeatType = useCallback(
    (value: RepeatType) => setValue('repeatType', value, { shouldValidate: false }),
    [setValue],
  );
  const setRecurrenceDays = useCallback(
    (value?: CreateVisitFormV2Values['recurrenceDays']) => setValue('recurrenceDays', value, { shouldValidate: false }),
    [setValue],
  );
  const clearRepeatEndDate = useCallback(() => setValue('repeatEndDate', null, { shouldValidate: false }), [setValue]);

  const repeatSection = useRepeatSection({
    visitDate,
    repeatType,
    recurrenceDays,
    setRecurrenceDays,
    clearRepeatEndDate,
  });
  const { repeatOptions, customRepeatDays, handleCustomDayToggle, handleRepeatChange } = repeatSection;

  // Max repeat date: 6 months from visit date, tightened by the building's
  // maxFutureInvite setting when configured. The whole-month count drives the
  // helper text so it matches whichever constraint is binding.
  let maxRepeatDate: Date | undefined;
  if (visitDate) {
    const sixMonthsOut = addMonths(visitDate, 6);
    maxRepeatDate =
      maxFutureInviteDate && isBefore(maxFutureInviteDate, sixMonthsOut) ? maxFutureInviteDate : sixMonthsOut;
  }

  const maxRepeatMonths =
    visitDate && maxRepeatDate ? Math.max(1, differenceInCalendarMonths(maxRepeatDate, visitDate)) : 6;

  return (
    <div className="space-y-4 mt-4">
      {/* Repeat Dropdown */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="repeatType">
            {formatMessage({ id: 'createVisitFormV2.whenStep.repeat.label', defaultMessage: 'Repeat' })}
          </Label>
          <Tip
            content={formatMessage({
              id: 'createVisitFormV2.whenStep.repeat.tooltip',
              defaultMessage:
                'Set up recurring visits that repeat on a schedule (daily, weekly, weekdays, or custom days)',
            })}
          >
            <Info className="h-4 w-4 text-muted-foreground" />
          </Tip>
        </div>
        <Controller
          name="repeatType"
          control={control}
          // eslint-disable-next-line react/jsx-no-bind
          render={({ field }) => (
            <Select
              value={field.value}
              onValueChange={(value) => {
                setRepeatType(value as RepeatType);
                handleRepeatChange(value as RepeatType);
              }}
            >
              <SelectTrigger id="repeatType" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {repeatOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {/* Custom Day Selection */}
      {repeatType === RepeatType.CUSTOM && (
        <div className="space-y-2">
          <div>
            <Label className="gap-1">
              {formatMessage({ id: 'createVisitFormV2.whenStep.repeat.selectDays', defaultMessage: 'Select days' })}
              <span className="text-destructive">*</span>
            </Label>
            <p className="text-xs text-muted-foreground mt-2">
              {formatMessage({
                id: 'createVisitFormV2.whenStep.repeat.selectDaysHelp',
                defaultMessage: 'Choose the days of the week when the visit should repeat. Select at least one day.',
              })}
            </p>
          </div>
          <Controller
            name="recurrenceDays"
            control={control}
            // eslint-disable-next-line react/jsx-no-bind
            render={({ field }) => (
              <div className="grid grid-cols-2 gap-3">
                {customRepeatDays.map((day) => (
                  <div key={day.key} className="flex items-center space-x-2 py-2">
                    <Checkbox
                      id={`day-${day.key}`}
                      checked={field.value?.[day.key] ?? false}
                      onCheckedChange={(checked) => {
                        const nextValue = handleCustomDayToggle(day.key, Boolean(checked));
                        setRecurrenceDays(nextValue);
                      }}
                    />
                    <label htmlFor={`day-${day.key}`} className="text-sm cursor-pointer">
                      {day.label}
                    </label>
                  </div>
                ))}
              </div>
            )}
          />
          {errors.recurrenceDays && (
            <p className="text-sm text-destructive mt-2">
              {formatValidationMessage(errors.recurrenceDays.message as string)}
            </p>
          )}
        </div>
      )}

      {/* Repeat Until Date */}
      {repeatType !== RepeatType.NONE && (
        <DatePickerField
          id="repeatEndDate"
          name="repeatEndDate"
          control={control}
          errors={errors}
          label={formatMessage({
            id: 'createVisitFormV2.whenStep.repeat.repeatUntil',
            defaultMessage: 'Repeat until',
          })}
          minDate={visitDate || new Date()}
          maxDate={maxRepeatDate}
          helperText={formatMessage(
            {
              id: 'createVisitFormV2.whenStep.repeat.repeatUntilHelp',
              defaultMessage:
                'The last date this recurring visit will occur (maximum {months, plural, one {# month} other {# months}} from first visit)',
            },
            { months: maxRepeatMonths },
          )}
          fixedLabelHeight={false}
          required
          popoverAlign="top"
        />
      )}
    </div>
  );
};
